import { useEffect, useState, type FormEvent } from "react";
import {
  investors as api,
  portfolio as portfolioApi,
  type Instrument,
  type Investor,
} from "../../api";
import { useRouter } from "../../router";

export function InvestorPicker() {
  const { navigate } = useRouter();
  const [items, setItems] = useState<Investor[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [dividendModal, setDividendModal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const handle = setTimeout(() => {
      api
        .list(search)
        .then((d) => {
          if (!cancelled) setItems(d.investors);
        })
        .catch((e) => {
          if (!cancelled) setError(e.message);
        });
    }, 120);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [search]);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <span className="eyebrow">Portfolio</span>
          <h1>Pick an investor</h1>
        </div>
        <div className="page-head-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => setDividendModal(true)}
          >
            Post dividend
          </button>
        </div>
      </header>

      <p className="muted">
        Drill into accounts and positions for any investor. Aggregated values are
        shown per investor on their portfolio page.
      </p>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Search investors"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="search"
        />
      </div>

      {error && <div className="banner-error">{error}</div>}

      {items === null ? (
        <div className="muted">Loading…</div>
      ) : items.length === 0 ? (
        <div className="empty">
          <p>No investors {search ? "match your search" : "yet"}.</p>
        </div>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>City</th>
              <th>Email</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr
                key={i.id}
                onClick={() => navigate(`/app/portfolio/${i.id}`)}
                className="row-link"
              >
                <td className="strong">{i.name}</td>
                <td>{i.city ?? "—"}</td>
                <td>{i.email ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {dividendModal && (
        <DividendModal onClose={() => setDividendModal(false)} />
      )}
    </div>
  );
}

// Corporate action entry: a cash dividend per unit, credited to the cash leg
// of every account holding the instrument.
function DividendModal({ onClose }: { onClose: () => void }) {
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [isin, setIsin] = useState("");
  const [perUnit, setPerUnit] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  useEffect(() => {
    portfolioApi
      .instruments()
      .then((d) => setInstruments(d.instruments))
      .catch(() => {});
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const amount = Number(perUnit);
    if (!isin) return setError("Pick an instrument.");
    if (!Number.isFinite(amount) || amount <= 0) {
      return setError("Dividend per unit must be a positive number.");
    }
    setBusy(true);
    setError(null);
    try {
      const { dividend } = await portfolioApi.postDividend(isin, amount);
      setResult(
        `${dividend.instrument}: credited ${dividend.accounts_credited} account(s), ` +
          `${dividend.total_paid.toLocaleString("en-GB")} in total.`,
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Post dividend</h2>
        <p className="muted">
          Credits the dividend to the cash account of every holder of the
          instrument.
        </p>
        {result ? (
          <>
            <div className="quiz-result">{result}</div>
            <div className="modal-actions">
              <button type="button" className="btn btn-primary" onClick={onClose}>
                Done
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={onSubmit} className="card-form" noValidate>
            <label className="field">
              <span>Instrument *</span>
              <select value={isin} onChange={(e) => setIsin(e.target.value)}>
                <option value="">Select instrument…</option>
                {instruments.map((i) => (
                  <option key={i.isin} value={i.isin}>
                    {i.name} ({i.currency})
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Dividend per unit *</span>
              <input
                type="number"
                min="0"
                step="any"
                value={perUnit}
                onChange={(e) => setPerUnit(e.target.value)}
              />
            </label>
            {error && <div className="banner-error">{error}</div>}
            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? "Posting…" : "Post dividend"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
